import {
  Badge,
  BadgeProps,
  HorizontalTabsList,
  LoadingIndicator,
  Tabs,
  TabsContent,
  TabsTrigger,
} from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { Country } from '@prisma/client';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FilingsTab,
  FilingTabItem,
  useFilingTabs,
} from '../../../hooks/useFilingTabs';
import { FilingsTable } from './FilingsTable';

type DisplayedFilingsTab = FilingsTab | 'ACTIVE';

type DisplayedFilingTabItem = Omit<FilingTabItem, 'id'> & {
  id: DisplayedFilingsTab;
};

const FILINGS_TAB_QUERY_KEY = 'filingsTab';

const FilingsTabs = ({
  businessEntityId,
  isAccountantAssignmentInProgress,
  incorporationCountry,
  loggedInUserEmail,
  loggedInUserId,
  refreshCompliances,
  selectedYear,
  showThunderIcon = true,
  isTNAApp = false,
  includeIndirectTaxFilings = false,
  includeHiddenComplianceTypes = false,
  tabContentClassName,
  stickyTabs = false,
  mergeActiveFilingTabs = false,
}: {
  businessEntityId: number;
  isAccountantAssignmentInProgress: boolean;
  incorporationCountry: Country;
  loggedInUserEmail: string;
  loggedInUserId: string;
  refreshCompliances: () => void;
  selectedYear?: number | null;
  showThunderIcon?: boolean;
  isTNAApp?: boolean;
  includeIndirectTaxFilings?: boolean;
  includeHiddenComplianceTypes?: boolean;
  tabContentClassName?: string;
  /**
   * Pin the tab bar to the top of the nearest scroll container while the
   * filings table scrolls underneath. Opt-in per page (e.g. compliance-monitor);
   * other consumers keep the default non-sticky layout.
   */
  stickyTabs?: boolean;
  /**
   * Collapse Upcoming/Overdue/Completed into a single "Active" tab so only
   * Active and Archived render.
   */
  mergeActiveFilingTabs?: boolean;
}) => {
  const router = useRouter();
  const [filters, setFilters] = useState<Record<string, string | string[]>>({});
  const { filingTabs, compliancesError, isLoading } = useFilingTabs({
    businessEntityId,
    year: selectedYear ?? undefined,
    includeIndirectTaxFilings,
    includeHiddenComplianceTypes,
  });

  const displayedFilingTabs = useMemo<DisplayedFilingTabItem[]>(() => {
    if (!mergeActiveFilingTabs) return filingTabs;

    const activeCompliances = filingTabs
      .filter((tab) => tab.id !== 'ARCHIVED')
      .flatMap((tab) => tab.compliances);
    const archivedTab = filingTabs.find((tab) => tab.id === 'ARCHIVED');

    return [
      {
        id: 'ACTIVE',
        label: 'Active',
        badge: { count: activeCompliances.length, variant: 'gray' },
        compliances: activeCompliances,
      },
      ...(archivedTab ? [archivedTab] : []),
    ];
  }, [filingTabs, mergeActiveFilingTabs]);

  const filingsTabParam = router.query[FILINGS_TAB_QUERY_KEY];
  const selectedFilingsTab = useMemo<DisplayedFilingsTab>(
    () =>
      displayedFilingTabs.find((tab) => tab.id === filingsTabParam)?.id ??
      (mergeActiveFilingTabs ? 'ACTIVE' : 'UPCOMING'),
    [displayedFilingTabs, filingsTabParam, mergeActiveFilingTabs]
  );

  const setSelectedFilingsTab = useCallback(
    (filingsTab: DisplayedFilingsTab) => {
      if (router.query[FILINGS_TAB_QUERY_KEY] === filingsTab) return;
      router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, [FILINGS_TAB_QUERY_KEY]: filingsTab },
        },
        undefined,
        { shallow: true }
      );
    },
    [router]
  );

  // A ?filingId= link names one filing, which may sit under a tab the user isn't on. Select the tab
  // holding it so the table below receives the row and can open it.
  const selectedFromQuery = useRef<string | null>(null);
  useEffect(() => {
    const { filingId } = router.query;
    if (typeof filingId !== 'string') {
      selectedFromQuery.current = null;
      return;
    }
    if (selectedFromQuery.current === filingId) return;

    const owningTab = displayedFilingTabs.find((tab) =>
      tab.compliances.some((compliance) => compliance.id === filingId)
    );
    if (!owningTab) return;

    selectedFromQuery.current = filingId;
    setSelectedFilingsTab(owningTab.id);
  }, [router.query.filingId, displayedFilingTabs, setSelectedFilingsTab]);

  if (isLoading || compliancesError)
    return <LoadingIndicator _className="mx-auto mt-12" size={64} />;

  const tabsList = (
    <HorizontalTabsList variant="underline" className="gap-lg px-3xl">
      {displayedFilingTabs.map((tab) => (
        <TabsTrigger key={tab.id} value={tab.id} className="translate-y-0.5">
          {tab.label}
          {tab.badge && tab.badge.count > 0 && (
            <Badge
              variant={tab.badge.variant as BadgeProps['variant']}
              size="sm"
            >
              {tab.badge.count}
            </Badge>
          )}
        </TabsTrigger>
      ))}
    </HorizontalTabsList>
  );

  return (
    <Tabs
      value={selectedFilingsTab}
      onValueChange={(value: DisplayedFilingsTab) => {
        setSelectedFilingsTab(value);
        setFilters({});
      }}
    >
      {stickyTabs ? (
        // z-[60] keeps the bar above the table's sticky group-header cells
        // (z-50); bg-bg-primary hides rows scrolling underneath.
        <div className="sticky top-0 z-[60] bg-bg-primary">{tabsList}</div>
      ) : (
        tabsList
      )}
      <TabsContent
        value={selectedFilingsTab}
        className={cn('mt-4xl', tabContentClassName)}
      >
        <FilingsTable
          filters={filters}
          setFilters={setFilters}
          compliances={
            displayedFilingTabs.find((tab) => tab.id === selectedFilingsTab)
              ?.compliances || []
          }
          showStatusColumn={
            selectedFilingsTab === 'UPCOMING' || selectedFilingsTab === 'ACTIVE'
          }
          refreshCompliances={refreshCompliances}
          isAccountantAssignmentInProgress={isAccountantAssignmentInProgress}
          incorporationCountry={incorporationCountry}
          loggedInUserEmail={loggedInUserEmail}
          loggedInUserId={loggedInUserId}
          businessEntityId={businessEntityId}
          showThunderIcon={showThunderIcon}
          isTNAApp={isTNAApp}
        />
      </TabsContent>
    </Tabs>
  );
};

export { FilingsTabs };
